import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { IsLoading, AccountDeactivated, NeedVerification } from 'components'
// import { NotFound } from 'views/Auth/NotFound'
import { useAuth } from 'contexts/AuthContext'
import { Login, ForgotPassword, ResetPassword, LoginMFACode, NotFound } from 'views/Auth'
import WebsiteApply from 'views/Auth/WebsiteApply/WebsiteApply'
import { useIdleSessionTimeout } from 'hooks/auth/useIdleSessionTimeout'
import { SupportRequest, NotificationCenter, NeedsResetPassword, AccountSettings } from 'views/Shared'
import { getSuperUserPages, clientPages, dealerPages, bankPages } from 'router/pages'
import {
  AuthRoute,
  SuperUserRoute,
  DealerRoute,
  ClientRoute,
  BankRoute,
  ProtectedRoute,
  SalesAgentRoute,
} from './routes'
import { mapPagesToReactRouter } from 'router/mapPagesToReactRouter'
import { parseInventoryV3Route, writeInventoryV3Route } from 'views/SuperAdmin/InventoryV3/inventoryV3Route'
import { DASHBOARD_LEGACY_PATH_TABS } from 'router/dashboardRoute'
import { AuthLayout } from './layouts'
import OnboardingLayout from './layouts/OnboardingLayout/OnboardingLayout'
import WebsiteApplyLayout from './layouts/WebsiteApplyLayout/WebsiteApplyLayout'
import { CoreLayout } from './layouts'
import { MainLoading } from '@/components/Loading/MainLoading'
import { readAuthSessionHint } from 'components/auth/authSessionHint'
import SessionExpired from 'components/SessionExpired'
import { ClientFinancingDetail } from 'views/SuperAdmin'
import { ClientOnboarding } from 'views/Client/ClientOnboarding/ClientOnboarding'
import { BankOnboarding } from 'views/Bank'
import FleetListPage from 'views/SuperAdmin/Management/SubmissionManagement/components/FleetListPage'
import PublicDeliveryTrackingPage from '@/views/Public/PublicDeliveryTrackingPage'
import PublicAssistantSharePage from '@/views/Public/PublicAssistantSharePage'
import { AssistantWorkspaceRoute } from 'components/assistant/AssistantWorkspaceRoute'

const AUTH_SURFACE_PATHS = new Set([
  '/',
  '/login',
  '/apply',
  '/forgot-password',
  '/reset-password',
  '/login-mfa-code',
])

const IdleSessionGuard = () => {
  useIdleSessionTimeout()
  return null
}

const RedirectKeepSearchHash = ({ to }: { to: string }) => {
  const { search, hash } = useLocation()
  return <Navigate to={`${to}${search}${hash}`} replace />
}

/** `/admin/sales` used to be a dashboard tab path. Inventory stays `/admin/inventory`. */
const RedirectLegacyDashboardTab = ({ tab }: { tab: string }) => {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  if (tab === 'overview') params.delete('tab')
  else params.set('tab', tab)
  const query = params.toString()
  return <Navigate to={query ? `/admin?${query}` : '/admin'} replace />
}

const RedirectInventoryVehicleToV3 = () => {
  const { vehicleId } = useParams()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  writeInventoryV3Route(params, {
    ...parseInventoryV3Route(params),
    open: 'edit',
    truckId: vehicleId ?? null,
    docTab: null,
  })
  const query = params.toString()
  return <Navigate to={query ? `/admin/inventory-v3?${query}` : '/admin/inventory-v3'} replace />
}

const AuthBootOverlay = () => {
  const { isLoading, currentUser } = useAuth()
  const location = useLocation()
  if (!isLoading) return null
  const hadSession = readAuthSessionHint() || Boolean(currentUser)
  if (
    !hadSession &&
    (AUTH_SURFACE_PATHS.has(location.pathname) || location.pathname.startsWith('/assistant/share/'))
  ) {
    return null
  }
  return <MainLoading />
}

export const AppRoutes = () => {
  // Move this before the early return to ensure consistent hook order
  const superuserPages = getSuperUserPages()

  // Keep the Router mounted during auth loading. Unmounting it on every
  // onAuthStateChanged (including multi-tab Firebase sync) destroys
  // navigation state and leaves users stuck on the logo splash.
  const AuthRoutes = () => {
    return (
      <>
        <Route
          path='/login'
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          }
        />
        {/* <Route
          path='/signup'
          element={
            <AuthRoute>
              <Signup />
            </AuthRoute>
          }
        /> */}
        <Route
          path='/forgot-password'
          element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          }
        />

        {/* <Route
          path='/reset-password'
          element={
            <AuthRoute>
              <ResetPassword />
            </AuthRoute>
          }
        /> */}

        <Route
          path='/reset-password'
          element={
            <AuthRoute>
              <ResetPassword />
            </AuthRoute>
          }
        />

        <Route
          path='/login-mfa-code'
          element={
            <AuthRoute>
              <LoginMFACode />
            </AuthRoute>
          }
        />
      </>
    )
  }

  const RedirectRoutes = () => {
    return (
      <>
        {/* Not Found - other pages redirect here */}
        <Route path='/not-found' element={<NotFound />} />

        <Route path='/session-expired' element={<SessionExpired />} />

        {/* Redirect logic to /login or /role occurs in AuthLayout */}
        <Route path='/' />

        {/* Redirect * to NotFound to hide SuperUser Routes  */}
        <Route path='*' element={<Navigate to='/not-found' replace />} />
      </>
    )
  }

  const SharedRoutes = () => {
    return (
      <>
        <Route
          path='/notification-center'
          element={
            <ProtectedRoute>
              <NotificationCenter />
            </ProtectedRoute>
          }
        />
        <Route
          path='/support-request'
          element={
            <ProtectedRoute>
              <SupportRequest />
            </ProtectedRoute>
          }
        />
        <Route
          path='/not-found'
          element={
            <ProtectedRoute>
              <NotFound />
            </ProtectedRoute>
          }
        />
        <Route
          path='/account-deactivated'
          element={
            <ProtectedRoute>
              <AccountDeactivated />
            </ProtectedRoute>
          }
        />

        <Route
          path='/account-settings'
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />
      </>
    )
  }

  const SuperAdminRoutes = () => {
    return (
      <>
        <Route
          path='/admin/assistant'
          element={
            <SuperUserRoute>
              <AssistantWorkspaceRoute />
            </SuperUserRoute>
          }
        />
        <Route
          path='/fleet-list'
          element={
            <SuperUserRoute>
              <FleetListPage />
            </SuperUserRoute>
          }
        />
        <Route
          path='/admin/financing-profiles/client/:clientId'
          element={
            <SuperUserRoute>
              <ClientFinancingDetail />
            </SuperUserRoute>
          }
        />
        <Route
          path='/admin/financing-profiles/company/:companyId/client/:clientId'
          element={
            <SuperUserRoute>
              <ClientFinancingDetail />
            </SuperUserRoute>
          }
        />
        <Route
          path='/admin/inventory'
          element={
            <SuperUserRoute>
              <RedirectKeepSearchHash to='/admin/inventory-v3' />
            </SuperUserRoute>
          }
        />
        {DASHBOARD_LEGACY_PATH_TABS.map(tab => (
          <Route
            key={tab}
            path={`/admin/${tab}`}
            element={
              <SuperUserRoute>
                <RedirectLegacyDashboardTab tab={tab} />
              </SuperUserRoute>
            }
          />
        ))}
        <Route
          path='/admin/inventory/:vehicleId'
          element={
            <SuperUserRoute>
              <RedirectInventoryVehicleToV3 />
            </SuperUserRoute>
          }
        />
        <Route
          path='/admin/shield-inventory-v2'
          element={
            <SuperUserRoute>
              <RedirectKeepSearchHash to='/admin/shield-inventory-v3' />
            </SuperUserRoute>
          }
        />
      </>
    )
  }

  const OnboardingRoutes = () => {
    return (
      <>
        <Route element={<OnboardingLayout />}>
          <Route path='/needs-reset-password' element={<NeedsResetPassword />} />
          <Route path='/client-onboarding' element={<ClientOnboarding />} />
          <Route path='/bank-onboarding' element={<BankOnboarding />} />
        </Route>{' '}
      </>
    )
  }

  return (
    <Router>
      <IdleSessionGuard />
      <AuthBootOverlay />
      <Routes>
        <Route path="/tracking/:token" element={<PublicDeliveryTrackingPage />} />
        <Route path="/assistant/share/:token" element={<PublicAssistantSharePage />} />
        <Route element={<WebsiteApplyLayout />}>
          <Route path='/apply' element={<WebsiteApply />} />
        </Route>
        <Route element={<AuthLayout />}>
          {AuthRoutes()} {RedirectRoutes()}
        </Route>

        {OnboardingRoutes()}

        <Route element={<CoreLayout />}>
          {SharedRoutes()}
          {SuperAdminRoutes()}
          {mapPagesToReactRouter(superuserPages, SuperUserRoute)}
          {mapPagesToReactRouter(clientPages, ClientRoute)}
          {mapPagesToReactRouter(clientPages, SalesAgentRoute)} // Just use client pages until scott needs to see
          different pages
          {mapPagesToReactRouter(dealerPages, DealerRoute)}
          {mapPagesToReactRouter(bankPages, BankRoute)}
        </Route>
      </Routes>
    </Router>
  )
}
